<template>
  <div
    v-if="infoText"
    :class="{
      infoBanner: true,
      'infoBanner-bar': variant === 'bar',
      'infoBanner-block': variant === 'block'
    }"
    data-testid="infoBanner"
  >
    <p>{{ infoText }}</p>
  </div>
</template>

<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Unsubscribe } from 'firebase/database';

import { rldb, ref, onValue } from '@/firebase/firebaseInit';

// Free-form notice maintained in the Realtime Database. Read straight from the
// `infoText` child, because that is the only part of /maintenanceInfo the
// database rules expose publicly. It may hold a plain string or an object
// keyed by locale (`{ en: '…', fi: '…' }`).
const INFO_TEXT_PATH = 'maintenanceInfo/infoText';

@Options({
  name: 'InfoBanner',
  props: {
    // 'bar' spans the top of the app, 'block' sits above the login form.
    variant: {
      type: String,
      default: 'bar'
    }
  },
  data() {
    return {
      infoText: '',
      rawInfoText: null,
      unsubscribe: null as Unsubscribe | null
    };
  },
  mounted() {
    this.unsubscribe = onValue(
      ref(rldb, INFO_TEXT_PATH),
      (snapshot) => {
        this.rawInfoText = snapshot.val();
        this.infoText = this.resolveInfoText(this.rawInfoText);
      },
      // The notice is optional — if it cannot be read (e.g. the database rules
      // deny anonymous access on the login page) simply show nothing.
      () => {
        this.rawInfoText = null;
        this.infoText = '';
      }
    );
  },
  unmounted() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  },
  methods: {
    resolveInfoText(value: unknown): string {
      if (typeof value === 'string') {
        return value.trim();
      }

      if (value && typeof value === 'object') {
        const byLocale = value as Record<string, unknown>;
        const text =
          byLocale[this.$i18n.locale] ??
          byLocale.en ??
          Object.values(byLocale)[0];

        return typeof text === 'string' ? text.trim() : '';
      }

      return '';
    }
  },
  watch: {
    '$i18n.locale'() {
      this.infoText = this.resolveInfoText(this.rawInfoText);
    }
  }
})
export default class InfoBannerComponent extends Vue {}
</script>

<style lang="scss" scoped>
.infoBanner {
  background: $backgroundPending;
  p {
    background: transparent;
    color: $main-text;
    font-size: $fourteenSize;
    line-height: 20px;
    white-space: pre-line;
  }
}

.infoBanner-bar {
  width: 100%;
  padding: 12px 24px;
  border-bottom: 1px solid $yellow;
  p {
    text-align: center;
  }
}

.infoBanner-block {
  margin-bottom: 16px;
  padding: 16px;
  border: 1px solid $yellow;
  border-radius: 8px;
}
</style>
